import {
  Combobox,
  ComboboxGroup,
  ComboboxLabels,
  ComboboxOption,
  ComboboxProps,
} from "../combobox/base";

export type SelectOption = ComboboxOption;
export type SelectGroup = ComboboxGroup;

export interface SelectProps extends Omit<
  ComboboxProps,
  "value" | "onChange" | "multiple" | "maxChips"
> {
  /** Selected value; `""` means no selection. */
  value?: string;
  /** Fires with the next selected value (`""` when cleared). */
  onChange?: (value: string) => void;
  /** Placeholder for the search box while filtering (maps to `labels.searchPlaceholder`). */
  searchPlaceholder?: string;
  /** No-match text (maps to `labels.empty`). */
  emptyLabel?: string;
}

/**
 * Single-value dropdown — the single-select sibling of the {@link Combobox},
 * built on the same field, portaled listbox, keyboard navigation and ARIA. Type
 * to filter (set `searchable={false}` for a plain dropdown); a checkmark marks
 * the chosen option. The parent owns `value` (`""` = nothing selected); reach
 * for `ControlledSelect` inside a form or `UncontrolledSelect` when the field is
 * self-contained.
 */
export function Select({
  value = "",
  onChange,
  required = false,
  clearable = true,
  searchPlaceholder,
  emptyLabel,
  labels,
  ...rest
}: SelectProps) {
  const mergedLabels: Partial<ComboboxLabels> = {
    ...labels,
    ...(searchPlaceholder ? { searchPlaceholder } : {}),
    ...(emptyLabel ? { empty: emptyLabel } : {}),
  };

  return (
    <Combobox
      {...rest}
      multiple={false}
      required={required}
      clearable={clearable && !required}
      labels={mergedLabels}
      value={value ? [value] : []}
      onChange={(next) => onChange?.(next[next.length - 1] ?? "")}
    />
  );
}

export default Select;
